package com.gsb.vyapar.bhagya.datasource.cache

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOne
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import com.gsb.vyapar.bhagya.datasource.network.DtoId
import com.gsb.vyapar.bhagya.domain.Categories
import com.gsb.vyapar.bhagya.domain.SubCategories
import com.gsb.vyapar.bhagya.domain.Business
import com.gsb.vyapar.bhagya.domain.toBusiness
import com.gsb.vyapar.bhagya.domain.toDtoId
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.launchDefault
import com.gsb.vyapar.bhagya.util.DatabaseDriverFactory
import kotlinx.coroutines.CompletableDeferred
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

object SqlDelightClient {
    private var database: GsbVyaparBhagyaDatabase? = null
    private var queries: GsbVyaparBhagyaDatabaseQueries? = null
    private var initDeferred: CompletableDeferred<Unit>? = null
    private val maxCacheSize: Int = 1000

    fun initDatabase() {
        if (initDeferred == null) {
            initDeferred = CompletableDeferred()
            launchDefault {
                database = GsbVyaparBhagyaDatabase(DatabaseDriverFactory().createDriver())
                initDeferred?.complete(Unit)
            }
        }
    }

    private suspend fun awaitInitialization() {
        initDeferred?.await()
    }

    private suspend fun getQueries(): GsbVyaparBhagyaDatabaseQueries? {
        awaitInitialization()
        if (queries == null) queries = database?.gsbVyaparBhagyaDatabaseQueries
        return queries
    }

    suspend fun insertOrUpdateCategory(category: Categories.D.Category) {
        if (category.id > 0) {
            getQueries()?.updateCategory(category.id, category.name, category.imageUrl, category.deleted)
        } else {
            getQueries()?.insertCategory(category.name, category.imageUrl, category.deleted)
        }
    }

    suspend fun updateCategories(categories: Categories) {
        categories.d.categories.forEach {
            insertOrUpdateCategory(it)
        }
    }

    suspend fun getCategories(): List<CategoryEntity> {
        return getQueries()?.getCategories()?.awaitAsList().orEmpty()
    }

    suspend fun getSubCategories(): List<SubCategoryEntity> {
        return getQueries()?.getSubCategories()?.awaitAsList().orEmpty()
    }

    suspend fun insertOrUpdateSubCategory(subCategory: SubCategories.D.SubCategory) {
        if (subCategory.id > 0) {
            getQueries()?.updateSubCategory(subCategory.id, subCategory.name, subCategory.categoryId, subCategory.deleted)
        } else {
            getQueries()?.insertSubCategory(subCategory.name, subCategory.categoryId, subCategory.deleted)
        }
    }

    suspend fun updateSubCategories(subCategories: SubCategories) {
        subCategories.d.subCategories.forEach {
            insertOrUpdateSubCategory(it)
        }
    }

    suspend fun getSubCategoriesOfCategory(categoryId: Long): List<SubCategoryEntity> {
        return getQueries()?.getSubCategoriesOfCategory(categoryId)?.awaitAsList().orEmpty()
    }

    @OptIn(ExperimentalTime::class)
    suspend fun putImage(url: String, data: ByteArray) {
        val currentTime = Clock.System.now().epochSeconds
        getQueries()?.updateImageCache(url, data, currentTime)
        val totalItems = getQueries()?.countImagesCache()?.awaitAsOne() ?: 0
        if (totalItems > maxCacheSize) {
            val itemsToRemove = totalItems - maxCacheSize
            getQueries()?.evictOldest(itemsToRemove)
        }
    }

    @OptIn(ExperimentalTime::class)
    suspend fun getImage(url: String): ByteArray? {
        val currentTime = Clock.System.now().epochSeconds
        val image = getQueries()?.getImage(url)?.awaitAsOneOrNull()
        if (image != null) {
            getQueries()?.updateAccessTime(currentTime, url)
        }
        return image
    }

    suspend fun updateBusiness(business: Business) {
        val dto = business.toDtoId()
        getQueries()?.updateBusiness(dto.i.toLong(), dto.v.toLong(), dto.u, dto.d)
    }

    suspend fun updateBusiness(dto: DtoId) {
        getQueries()?.updateBusiness(dto.i.toLong(), dto.v.toLong(), dto.u, dto.d)
    }

    suspend fun getBusiness(id: Int): Business? {
        val entity = getQueries()?.getBusiness(id.toLong())?.awaitAsOneOrNull()
        return entity?.let {
            val dto = DtoId(
                v = it.v.toInt(),
                d = it.d,
                i = it.i.toInt(),
                u = it.u
            )
            dto.toBusiness()
        }
    }

    suspend fun getAllTheBusinesses(): List<Business> {
        return getQueries()?.getAllTheBusinesses()?.awaitAsList()?.map {
            val dto = DtoId(
                v = it.v.toInt(),
                d = it.d,
                i = it.i.toInt(),
                u = it.u
            )
            dto.toBusiness()
        }.orEmpty()
    }

    suspend fun clearDB() {
        getQueries()?.let {
            it.clearCategories()
            it.clearSubCategories()
            it.clearImageCache()
            it.clearBusiness()
        }
    }
}